import { z } from 'zod';

// Cloudflare Turnstile on the signup + WA checkout gates (components/Turnstile).
// Naming: domain.subject_verb, past tense — events describe what HAPPENED.
//
// This widget is invisible in managed mode, so when it dies there is nothing on
// screen and the visitor simply leaves. Every dead end it has produced was
// found late and by hand: the 600010 in-app-webview failure hid the WA
// checkout's zero purchases for six days, and on 2026-08-02 four visitors hit a
// silent stall on /start/* and none of them submitted an email. These events
// are the only reason either was findable at all, which is why they belong in
// the catalog rather than in hand-named capture() strings.
//
// LEGACY NAMES: these predate the catalog and carry 30+ days of production
// history plus the daily ads-health checks that query them, so they publish
// under their original flat PostHog names via the rename map below. The catalog
// name is what code uses; the wire name is unchanged. Renaming in PostHog is a
// separate decision from adopting the catalog — don't couple them.

// Which of the two dead ends a visitor hit. A hard failure returns a Cloudflare
// error code; a stall returns nothing at all, which is precisely what made it
// invisible before it was instrumented.
const attempt = z.number().int().positive();
const inApp = z.boolean();
/**
 * Which Turnstile site key issued this challenge. Optional and only present
 * from 2026-09-09 on (brain #960): the in-app-webview Non-Interactive-mode
 * experiment in components/Turnstile.tsx, utils/turnstileVariant.ts. A row
 * with no `variant` predates this and was on the managed key — the only key
 * that has ever existed until now.
 */
const variant = z.enum(['inapp', 'managed']).optional();

export const TURNSTILE_LEGACY_POSTHOG_NAMES: Readonly<
  Partial<Record<keyof typeof turnstileSchemas, string>>
> = {
  'turnstile.challenge_failed': 'turnstile_error',
  'turnstile.challenge_stalled': 'turnstile_stalled',
  'turnstile.escape_link_copied': 'turnstile_copy_link_clicked',
  'turnstile.retry_clicked': 'turnstile_retry_clicked',
  'turnstile.webview_blocked': 'turnstile_webview_blocked',
};

export const turnstileSchemas = {
  /**
   * The challenge hard-failed with a Cloudflare error code. 110200 is
   * domain-not-allowed and means localhost or a Railway preview, NOT a real
   * user — the site key allowlists onark.app only. Filter it out before
   * reading this as user-facing breakage.
   */
  'turnstile.challenge_failed': z.object({
    attempt,
    /** Cloudflare's code, or 'unknown' when it hands us nothing. */
    code: z.string(),
    inApp,
  }),
  /**
   * The challenge had not resolved when the stall window elapsed — neither
   * callback had fired yet. The silent path: no code, nothing on screen.
   *
   * ⚠️ **This is a stopwatch, not a verdict.** A wall-clock window cannot tell
   * a dead widget from a live one that is waiting on a human: managed Turnstile
   * escalates some visitors to an INTERACTIVE challenge, which sits unresolved
   * until they click it. Read `fatal` before treating a row as breakage.
   */
  'turnstile.challenge_stalled': z.object({
    /** How many times THIS widget instance has passed its stall window. */
    attempt,
    /**
     * Whether this expiry ended the challenge. `true` only in a webview, where
     * the challenge genuinely cannot solve and the escape has to appear at
     * once. `false` in a real browser: telemetry only — the widget is left
     * running, no token is cleared, and it can still solve afterwards. A
     * `fatal: false` row is a slow challenge, NOT a lost visitor.
     */
    fatal: z.boolean(),
    inApp,
    /**
     * The stall window that actually elapsed — **bimodal, not one constant**:
     * `4000` for an in-app webview, `60000` for a real browser. The webview
     * leash is short because there a stall is the normal case, not the rare one
     * worth waiting out (PR #260). The browser backstop is long because killing
     * a live challenge on a stopwatch is what it used to do wrong: at 15000 it
     * fired on interactive challenges that were still in flight, told the
     * visitor a working check was broken, and offered a retry that destroyed
     * it — measured 2026-08-08, one visitor ground that loop 7 times in 2
     * minutes and left. Read `ms` alongside `inApp`; an aggregate over this
     * field alone mixes two populations.
     */
    ms: z.number().int().positive(),
    variant,
  }),
  /**
   * Cloudflare's own `timeout-callback`: the interactive challenge expired
   * unsolved. THIS is the authoritative "no token is coming" signal for a real
   * browser — unlike the stall stopwatch above, it comes from the widget.
   */
  'turnstile.challenge_timed_out': z.object({ attempt, inApp }),
  /**
   * A token arrived: the challenge SOLVED, with how long it took.
   *
   * The denominator this catalog went without. Every other event here records
   * a challenge that FAILED, so every stall window the component has shipped —
   * 15s, then 60s, and the original 4s webview leash — was argued for rather
   * than derived, and all three turned out to be cutting live challenges.
   *
   * ⚠️ **Read `ms` split by `inApp` and filtered to `refresh: false`.** On
   * 2026-08-11 a challenge was watched solving inside the Instagram webview on
   * iOS 26, which disproved "the challenge cannot solve in a webview at all" —
   * the premise the original 4s leash's fatal verdict rested on. **Settled
   * 2026-08-24** (30d, n=211/193 people): p50 1,724ms, p75 2,728ms, p90
   * 4,171ms, p95 8,536ms — webview challenges DO regularly solve past 4s, so
   * the leash was failing live challenges, not just calling dead ones.
   * `components/Turnstile.tsx` moved `IN_APP_STALL_MS` to 10s off this
   * measurement; re-read this metric if that constant ever moves again.
   *
   * Not comparable across populations naively: a real browser defers the
   * widget until the visitor engages, while a webview pre-arms it at mount
   * (PR #285), so the two clocks start at different moments in the visit.
   *
   * ⚠️ **Rows with NO `refresh` property are mount-inflated — exclude them.**
   * That absence dates them exactly, which is why it beats a timestamp cutoff:
   * they predate this field. The clock used to be stamped at render only, so
   * Cloudflare's automatic token refresh (a fresh challenge about every 5
   * minutes) kept reporting time since MOUNT — one visitor's 11th solve read
   * 24,406,124ms, 6.8 hours, and the median over all 36 rows read 5.9 minutes,
   * measuring how long people leave a tab open. Their FIRST solve per person is
   * still sound (1,069–2,108ms, n=12, all webview); every later one is that
   * number plus idle time.
   */
  'turnstile.challenge_solved': z.object({
    inApp,
    /** Milliseconds from the start of THIS challenge to its token. */
    ms: z.number().int().nonnegative(),
    /**
     * Whether this solve came from Cloudflare's automatic token refresh rather
     * than the challenge the visitor actually waited on.
     *
     * The discriminator, not a nicety: **only `false` rows can speak to the
     * leash.** The stall window is armed at render and on retry, never on a
     * refresh, so a refresh solve is never the one the 4s leash is racing —
     * pooling it in would let an idle tab's slow refresh read as "the leash is
     * cutting live challenges". It also makes the inflation bug self-evident
     * if it ever returns: an absurd `ms` on a `refresh: true` row means the
     * expiry that should have restarted the clock never fired.
     */
    refresh: z.boolean(),
    variant,
  }),
  /**
   * The Cloudflare script itself never loaded — `window.turnstile` was still
   * absent after the load window, so no widget was ever rendered and no
   * challenge callback could ever fire.
   *
   * Its own event because it is invisible to every other one here: the stall
   * timer is armed inside `render()`, so a blocked script emits NOTHING at all
   * and the visitor just gets a form that refuses to submit. Expect an ad
   * blocker, a DNS filter, or a locked-down church/office network.
   */
  'turnstile.script_blocked': z.object({
    attempt,
    inApp,
    /** How long we waited for `window.turnstile` before calling it blocked. */
    ms: z.number().int().positive(),
  }),
  /**
   * An in-app browser (Facebook/Instagram/TikTok) blocked the challenge. Split
   * from challenge_failed because it is the one failure a retry can never fix.
   */
  'turnstile.webview_blocked': z.object({ attempt, code: z.string() }),
  /** The visitor pressed "Try again". Offered once — see the component. */
  'turnstile.retry_clicked': z.object({ attempt }),
  /**
   * The visitor copied the page link to escape to another browser. The only
   * success signal on the dead-end path: it means the escape hatch worked.
   */
  'turnstile.escape_link_copied': z.object({ attempt, inApp }),
  /**
   * The visitor tapped "Open in Safari/browser" — the one-tap escape that
   * tries to hand the page to the real browser via a platform deep link
   * (x-safari-https:// on iOS, intent:// on Android). The deep link is a
   * best-effort no-op where the webview ignores the scheme, so this event
   * means "attempted", not "arrived" — a signup finished from a Safari
   * session is the only arrival signal. Copy-link stays the fallback and
   * keeps its own event above.
   */
  'turnstile.escape_opened': z.object({
    attempt,
    inApp,
    /** Which deep-link scheme was attempted. */
    platform: z.enum(['ios', 'android']),
  }),
  /**
   * The deep link went nowhere: `escape_opened` fired, and the page was still
   * VISIBLE when the check below elapsed — so the webview swallowed the scheme
   * instead of handing off. A successful escape backgrounds the app, which
   * flips `document.visibilityState` to hidden, so this fires only on the
   * failure.
   *
   * The pair (`escape_opened` − `escape_stalled`) is the first thing here that
   * can separate "the visitor never tapped" from "the visitor tapped and the
   * scheme did nothing" — two numbers that used to be one, and that point at
   * opposite fixes (surface the CTA vs. abandon the deep link and lead with
   * the manual instruction).
   */
  'turnstile.escape_stalled': z.object({
    attempt,
    inApp,
    /** How long we waited for the handoff before calling it a no-op. */
    ms: z.number().int().positive(),
    platform: z.enum(['ios', 'android']),
  }),
  /**
   * A page loaded carrying the escape marker — i.e. somebody who dead-ended in
   * a webview actually ARRIVED in another browser.
   *
   * The denominator the escape has never had. Every other event on this path
   * records an ATTEMPT (`escape_opened` is explicit that it means "tried", and
   * a copied link is only intent), so the escape's true take rate has never
   * been computable: a tap that silently no-ops and a tap that lands in Safari
   * emitted exactly the same thing. Read it against `escape_opened` +
   * `escape_link_copied` over the same window.
   *
   * ⚠️ Fires per PAGELOAD carrying the marker, not per person — a refresh
   * re-emits, because the marker is deliberately left on the URL so the
   * attribution it carries survives one too. Count unique persons.
   */
  'turnstile.escape_landed': z.object({
    /**
     * `true` means the escape landed back INSIDE a webview — the handoff went
     * to another in-app browser rather than a real one, so the visitor is no
     * better off. Expected to be rare; if it isn't, the deep link is wrong.
     */
    inApp,
    /**
     * The ad door, so a recovered visitor is attributable to the campaign that
     * paid for them. The only field here borrowed from the signup funnel's
     * vocabulary — arrival is where this path rejoins that funnel.
     */
    landing: z.string().optional(),
  }),
};
